import { useState, useEffect } from 'react';
import { Mail, Send, X, Clock, AlertCircle, CheckCircle, Calendar } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const EMAIL_TEMPLATES = [
  { value: 'manual', label: 'Email libre', icon: '✉️', category: 'general' },
  { value: 'welcome', label: 'Bienvenue', icon: '🎉', category: 'general' },
  { value: 'trial_end_reminder', label: 'Rappel fin période d\'essai', icon: '⏰', category: 'trial' },
  { value: 'profile_incomplete', label: 'Profil incomplet', icon: '📋', category: 'reminder' },
  { value: 'mission_not_validated', label: 'Mission non validée', icon: '🚗', category: 'reminder' },
  { value: 'training_reminder', label: 'Rappel de formation', icon: '📚', category: 'training' },
  { value: 'training_binome', label: 'Formation binôme Amazon', icon: '🚚', category: 'training' },
  { value: 'training_assignment', label: 'Inscription formation', icon: '📝', category: 'training' },
  // New HR warning templates
  { value: 'warning_absence', label: 'Avertissement absence', icon: '⚠️', category: 'warning' },
  { value: 'warning_lateness', label: 'Avertissement retard', icon: '⚠️', category: 'warning' },
  { value: 'warning_performance', label: 'Insuffisance professionnelle', icon: '⚠️', category: 'warning' },
  { value: 'summons_phone', label: 'Convocation téléphonique', icon: '📞', category: 'summons' },
];

const TEMPLATE_CATEGORIES = {
  general: { label: 'Général', color: 'bg-gray-100' },
  trial: { label: 'Période d\'essai', color: 'bg-blue-100' },
  reminder: { label: 'Rappels', color: 'bg-yellow-100' },
  training: { label: 'Formation', color: 'bg-green-100' },
  warning: { label: 'Avertissements', color: 'bg-red-100' },
  summons: { label: 'Convocations', color: 'bg-purple-100' },
};

const SendEmailModal = ({ isOpen, onClose, employee, onEmailSent }) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [template, setTemplate] = useState('manual');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setSubject('');
      setContent('');
      setTemplate('manual');
      setIsScheduled(false);
      setScheduledDate('');
      setScheduledTime('09:00');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  // Auto-fill subject based on template
  useEffect(() => {
    if (template !== 'manual') {
      const templateInfo = EMAIL_TEMPLATES.find(t => t.value === template);
      if (templateInfo) {
        setSubject(`${templateInfo.icon} ${templateInfo.label}`);
      }
    }
  }, [template]);

  const handleSend = async () => {
    if (!subject.trim()) {
      setError('Veuillez saisir un objet');
      return;
    }

    if (template === 'manual' && !content.trim()) {
      setError('Veuillez saisir un contenu');
      return;
    }

    if (isScheduled && !scheduledDate) {
      setError('Veuillez sélectionner une date d\'envoi');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        user_id: employee.user_id,
        subject: subject,
        content: content,
        template: template,
      };

      if (isScheduled && scheduledDate) {
        payload.scheduled_date = `${scheduledDate} ${scheduledTime}`;
      }

      const response = await axios.post(`${API}/emails/send`, payload, {
        withCredentials: true
      });

      if (response.data.status === 'sent' || response.data.status === 'simulated') {
        setSuccess('Email envoyé avec succès !');
      } else if (response.data.status === 'scheduled') {
        setSuccess(`Email programmé pour le ${scheduledDate} à ${scheduledTime}`);
      }

      if (onEmailSent) {
        onEmailSent();
      }

      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Failed to send email:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const employeeName = employee?.first_name && employee?.last_name 
    ? `${employee.first_name} ${employee.last_name}`
    : employee?.name || 'Employé';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] p-5 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-lg font-bold text-white">Envoyer un email</h2>
                <p className="text-sm text-gray-300">À : {employeeName} ({employee?.email})</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          {/* Template Selection by Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type d'email
            </label>
            
            {/* Category tabs */}
            <div className="flex flex-wrap gap-1 mb-3">
              {Object.entries(TEMPLATE_CATEGORIES).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => {
                    const firstInCategory = EMAIL_TEMPLATES.find(t => t.category === key);
                    if (firstInCategory) setTemplate(firstInCategory.value);
                  }}
                  className={`px-3 py-1 text-xs rounded-full ${cat.color} ${
                    EMAIL_TEMPLATES.find(t => t.value === template)?.category === key
                      ? 'ring-2 ring-blue-500 font-bold'
                      : ''
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {EMAIL_TEMPLATES.map(t => {
                const cat = TEMPLATE_CATEGORIES[t.category] || {};
                return (
                  <button
                    key={t.value}
                    onClick={() => setTemplate(t.value)}
                    className={`p-2 text-left rounded-lg border transition-all ${
                      template === t.value 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : `border-gray-200 hover:border-gray-300 ${cat.color || 'hover:bg-gray-50'}`
                    }`}
                  >
                    <span className="text-base">{t.icon}</span>
                    <span className="text-xs block mt-0.5 leading-tight">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Objet <span className="text-red-500">*</span>
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet de l'email"
              className="w-full"
            />
          </div>

          {/* Content - show for manual and warning templates */}
          {(template === 'manual' || template.startsWith('warning_') || template === 'summons_phone') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenu {template === 'manual' ? <span className="text-red-500">*</span> : <span className="text-gray-400">(personnalisable)</span>}
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  template === 'warning_absence' ? "Détaillez les circonstances de l'absence..." :
                  template === 'warning_lateness' ? "Précisez les retards constatés..." :
                  template === 'warning_performance' ? "Décrivez les problèmes de performance..." :
                  template === 'summons_phone' ? "Motif de la convocation..." :
                  "Rédigez votre message..."
                }
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
              />
              {template !== 'manual' && (
                <p className="text-xs text-gray-500 mt-1">
                  Ce contenu sera intégré dans le template "{EMAIL_TEMPLATES.find(t => t.value === template)?.label}".
                </p>
              )}
            </div>
          )}

          {/* Template Info - only for non-manual, non-warning templates */}
          {template !== 'manual' && !template.startsWith('warning_') && template !== 'summons_phone' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                <strong>📧 Email automatique</strong><br />
                Un email pré-formaté sera envoyé avec les informations du salarié.
                {template === 'trial_end_reminder' && ' Inclut la date de fin de période d\'essai.'}
                {template === 'profile_incomplete' && ' Inclut la liste des champs manquants.'}
                {template.includes('training') && ' Inclut les détails de la formation.'}
              </p>
            </div>
          )}

          {/* Warning templates info */}
          {(template.startsWith('warning_') || template === 'summons_phone') && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-700">
                <strong>
                  {template === 'summons_phone' ? '📞 Convocation officielle' : '⚠️ Avertissement officiel'}
                </strong><br />
                Ce document sera conservé dans le dossier du salarié. 
                Personnalisez le contenu ci-dessus si nécessaire.
              </p>
            </div>
          )}

          {/* Scheduled Option */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Programmer l'envoi</span>
              </div>
            </label>

            {isScheduled && (
              <div className="mt-4 flex gap-4 ml-7">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-xs text-gray-500 mb-1">Heure</label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">{success}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Annuler
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Envoi...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {isScheduled ? 'Programmer' : 'Envoyer'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
